import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { ChevronDown, Globe, LogOut, SlidersHorizontal } from 'lucide-react';
import { useToast } from '@/components/ui/toast';
import { initials } from '@/lib/domain/format';
import type { Me } from '@/features/identity/useMe';

/** Fecha o popover ao clicar fora ou ao apertar Esc (mesmo padrão do Select.tsx). */
function useOutsideClose(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onMouse = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  return ref;
}

function MenuItem({
  onClick,
  icon,
  danger = false,
  children,
}: {
  onClick: () => void;
  icon: ReactNode;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-[12.5px] hover:bg-secondary ${
        danger ? 'text-destructive' : 'text-foreground'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

/**
 * Menu de usuário do Topbar (mock §3.2 / app.jsx UserMenu): avatar + chevron que abrem um dropdown
 * com a identidade (nome/papel/e-mail), preferências como placeholders (Onda 3) e "Sair".
 * "Sair" encerra a sessão via MSAL — logoutRedirect leva ao postLogoutRedirectUri (= origin),
 * já configurado em msal-config.ts e registrado como redirect URI do SPA em dev e prod.
 */
export function UserMenu({ user }: { user?: Me }) {
  const { instance } = useMsal();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const ref = useOutsideClose(open, () => setOpen(false));

  const name = user?.name ?? '—';
  const role = user?.role ?? '';

  function handleLogout() {
    setOpen(false);
    void instance.logoutRedirect();
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menu do usuário"
        className="flex items-center gap-2 rounded-full py-0.5 pl-0.5 pr-1.5 hover:bg-secondary"
      >
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gold text-[11px] font-semibold text-gold-foreground">
          {initials(user?.name)}
        </div>
        <ChevronDown size={12} className="text-muted-foreground" strokeWidth={1.6} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Menu do usuário"
          className="absolute right-0 top-[calc(100%+6px)] z-50 w-60 overflow-hidden rounded-md border border-border bg-card shadow-lg"
        >
          <div className="border-b border-border px-3 py-2.5">
            <div className="mb-1 flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gold text-[11px] font-semibold text-gold-foreground">
                {initials(user?.name)}
              </div>
              <div className="leading-tight">
                <p className="text-xs font-semibold">{name}</p>
                <p className="text-[10px] text-muted-foreground">{role || '—'}</p>
              </div>
            </div>
            {user?.email && (
              <p className="font-mono text-[10.5px] text-muted-foreground">{user.email}</p>
            )}
          </div>

          <div className="p-1">
            <MenuItem
              icon={<SlidersHorizontal size={13} strokeWidth={1.6} />}
              onClick={() => {
                setOpen(false);
                toast('Preferências pessoais — em breve');
              }}
            >
              Preferências pessoais
            </MenuItem>
            <MenuItem
              icon={<Globe size={13} strokeWidth={1.6} />}
              onClick={() => {
                setOpen(false);
                toast('Locale de data e número — em breve');
              }}
            >
              Locale de data e número
            </MenuItem>
            <div className="my-1 h-px bg-border" />
            <MenuItem icon={<LogOut size={13} strokeWidth={1.6} />} danger onClick={handleLogout}>
              Sair
            </MenuItem>
          </div>
        </div>
      )}
    </div>
  );
}
